const MAX_EDGE = 2048
const JPEG_QUALITY = 0.92

function readArrayBuffer (blob) {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer()
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

function isJpegFile (file) {
  const type = (file.type || '').toLowerCase()
  if (type === 'image/jpeg' || type === 'image/jpg' || type === 'image/pjpeg') {
    return true
  }
  return /\.jpe?g$/i.test(file.name || '')
}

/**
 * Read EXIF orientation (1–8) from a JPEG buffer. Returns 1 when missing or not JPEG.
 */
export function getJpegOrientation (buffer) {
  const view = new DataView(buffer)
  if (view.byteLength < 2 || view.getUint16(0, false) !== 0xFFD8) {
    return 1
  }
  const length = view.byteLength
  let offset = 2
  while (offset + 4 <= length) {
    const marker = view.getUint16(offset, false)
    offset += 2
    if ((marker & 0xFF00) !== 0xFF00) {
      break
    }
    if (marker === 0xFFDA) {
      break
    }
    const size = view.getUint16(offset, false)
    if (size < 2 || offset + size > length) {
      break
    }
    if (marker === 0xFFE1 && size >= 10) {
      const isExif = view.getUint32(offset + 2, false) === 0x45786966 &&
        view.getUint16(offset + 6, false) === 0
      if (isExif) {
        const tiffOffset = offset + 8
        const little = view.getUint16(tiffOffset, false) === 0x4949
        if (view.getUint16(tiffOffset + 2, little) === 0x002A) {
          const ifd0 = tiffOffset + view.getUint32(tiffOffset + 4, little)
          if (ifd0 + 2 <= length) {
            const entries = view.getUint16(ifd0, little)
            for (let i = 0; i < entries; i++) {
              const entry = ifd0 + 2 + i * 12
              if (entry + 12 > length) {
                break
              }
              if (view.getUint16(entry, little) === 0x0112) {
                const value = view.getUint16(entry + 8, little)
                return value >= 1 && value <= 8 ? value : 1
              }
            }
          }
        }
      }
    }
    offset += size
  }
  return 1
}

export function getJpegSize (buffer) {
  const view = new DataView(buffer)
  if (view.byteLength < 2 || view.getUint16(0, false) !== 0xFFD8) {
    return null
  }
  const length = view.byteLength
  let offset = 2
  while (offset + 8 <= length) {
    const marker = view.getUint16(offset, false)
    offset += 2
    if ((marker & 0xFF00) !== 0xFF00) {
      break
    }
    const size = view.getUint16(offset, false)
    if (marker === 0xFFC0 || marker === 0xFFC1 || marker === 0xFFC2) {
      return {
        height: view.getUint16(offset + 3, false),
        width: view.getUint16(offset + 5, false)
      }
    }
    if (size < 2) {
      break
    }
    offset += size
  }
  return null
}

function scaledSize (width, height) {
  const edge = Math.max(width, height)
  if (edge <= MAX_EDGE) {
    return { width, height }
  }
  const scale = MAX_EDGE / edge
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  }
}

function jpegFileName (name) {
  if (!name) {
    return 'avatar.jpg'
  }
  return name.replace(/\.[^./\\]+$/, '') + '.jpg'
}

function canvasToJpegFile (canvas, originalFile) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to encode image'))
        return
      }
      const name = jpegFileName(originalFile && originalFile.name)
      if (typeof File === 'function') {
        resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }))
      } else {
        blob.name = name
        resolve(blob)
      }
    }, 'image/jpeg', JPEG_QUALITY)
  })
}

function drawToCanvas (source, width, height) {
  const size = scaledSize(width, height)
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height
  canvas.getContext('2d').drawImage(source, 0, 0, size.width, size.height)
  return canvas
}

function loadHtmlImage (file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to decode image'))
    }
    img.src = url
  })
}

function drawWithOrientation (img, orientation) {
  const src = scaledSize(img.naturalWidth, img.naturalHeight)
  const swapped = orientation >= 5 && orientation <= 8
  const canvas = document.createElement('canvas')
  canvas.width = swapped ? src.height : src.width
  canvas.height = swapped ? src.width : src.height
  const ctx = canvas.getContext('2d')
  switch (orientation) {
    case 2:
      ctx.translate(src.width, 0)
      ctx.scale(-1, 1)
      break
    case 3:
      ctx.translate(src.width, src.height)
      ctx.rotate(Math.PI)
      break
    case 4:
      ctx.translate(0, src.height)
      ctx.scale(1, -1)
      break
    case 5:
      ctx.rotate(0.5 * Math.PI)
      ctx.scale(1, -1)
      break
    case 6:
      ctx.rotate(0.5 * Math.PI)
      ctx.translate(0, -src.height)
      break
    case 7:
      ctx.rotate(0.5 * Math.PI)
      ctx.translate(src.width, -src.height)
      ctx.scale(-1, 1)
      break
    case 8:
      ctx.rotate(-0.5 * Math.PI)
      ctx.translate(-src.width, 0)
      break
  }
  ctx.drawImage(img, 0, 0, src.width, src.height)
  return canvas
}

async function orientJpegWithImageElement (file, orientation) {
  const img = await loadHtmlImage(file)
  const header = await readArrayBuffer(file.slice(0, 128 * 1024))
  const raw = getJpegSize(header)
  const swapped = orientation >= 5 && orientation <= 8
  const browserApplied = swapped && raw &&
    img.naturalWidth === raw.height && img.naturalHeight === raw.width
  const canvas = browserApplied
    ? drawToCanvas(img, img.naturalWidth, img.naturalHeight)
    : drawWithOrientation(img, orientation)
  return canvasToJpegFile(canvas, file)
}

/**
 * Bake EXIF orientation into pixel data so portrait photos stay upright
 * after upload/resize. Returns the original file when no correction is needed.
 */
export async function orientImageFile (file) {
  if (!file || typeof file.slice !== 'function') {
    return file
  }
  try {
    if (!isJpegFile(file)) {
      return file
    }
    const header = await readArrayBuffer(file.slice(0, 128 * 1024))
    const orientation = getJpegOrientation(header)
    if (orientation <= 1) {
      return file
    }
    if (typeof createImageBitmap === 'function') {
      try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
        const raw = getJpegSize(header)
        const swapped = orientation >= 5 && orientation <= 8
        const ignoredExif = swapped && raw &&
          bitmap.width === raw.width && bitmap.height === raw.height
        if (ignoredExif) {
          bitmap.close()
        } else {
          const canvas = drawToCanvas(bitmap, bitmap.width, bitmap.height)
          bitmap.close()
          return await canvasToJpegFile(canvas, file)
        }
      } catch (e) {
        // Older WebViews: decode via Image and rotate if EXIF was not applied.
      }
    }
    return await orientJpegWithImageElement(file, orientation)
  } catch (e) {
    return file
  }
}
